import { PaletteOptions } from '@mui/material/styles';

/**
 * Clarity palettes (light + dark).
 *
 * Visual direction: bright, airy, modern SaaS (the calm of Linear / Stripe).
 * Soft neutral light canvas, generous whitespace, and ONE confident accent — a
 * fresh periwinkle indigo — carried consistently for primary actions, links,
 * focus rings, and the chat entry. Light mode is the default and the hero.
 *
 * Design rules baked in here:
 *  - The primary ACTION color is a periwinkle indigo. LIGHT mode uses
 *    `#4F5BD5` carried with WHITE text (5.5:1, AA), readable both as a
 *    contained-button fill and as outlined text/border on the soft canvas.
 *    DARK mode lifts it to a bright `#818CF8` carried with NEAR-BLACK text
 *    (`#0A0F2C`, 6.2:1) per the bright-fill/dark-text convention, while still
 *    reading strongly as outlined text on the near-black canvas (6.4:1).
 *  - `secondary` is a fresh cobalt blue — a quiet sibling of the indigo — used
 *    for links / secondary actions. `info` shares that blue family.
 *  - Semantic success / warning / error stay conventional in HUE
 *    (green / amber / red) and AA-safe; white text on the light mains, the
 *    palette's bright-fill/dark-text convention on the dark mains.
 *  - `accent.highlight` is the indigo itself (sparing highlight use only); it
 *    is NOT spread across the app — wash / hover / focus surfaces derive a
 *    subtle low-alpha indigo tint from `primary` instead.
 *  - Surfaces are calm cool neutrals: a soft off-white canvas with white cards
 *    in light; a cool near-black canvas with charcoal cards in dark.
 *  - `cat.*` workflow-category hues are bright accents, IDENTICAL in both modes
 *    (saturated enough to read as small chips/badges on either canvas). They
 *    are intentionally left vivid so the Saved-Workflows category badges stay
 *    distinguishable; the calm indigo identity governs everything else.
 *  - `diff.*` carries the SOW reviewer green/red/blue semantics outside the
 *    always-light editor canvas.
 *
 * These objects intentionally contain COLORS ONLY. No shape/spacing/typography.
 */

export type ColorMode = 'light' | 'dark';

export const lightPalette: PaletteOptions = {
  mode: 'light',
  background: {
    default: '#F7F8FA', // soft cool off-white canvas — airy, breathable
    paper: '#FFFFFF', // white card
  },
  divider: '#E6E8EE', // cool hairline border
  text: {
    primary: '#101322', // cool near-black ink (faint indigo tint for cohesion)
    secondary: '#5A6072', // cool slate (6.5:1 on white, AA)
    disabled: '#A4A9B8',
  },
  primary: {
    // Periwinkle indigo — the single confident accent. White text clears AA on
    // it (5.5:1) both as a contained-button fill and as outlined text/border on
    // the soft canvas (~5.3:1 vs #F7F8FA, 5.5:1 vs #FFFFFF).
    main: '#4F5BD5',
    light: '#E9EBFB', // pale indigo wash — outlined hover, tonal fills
    dark: '#3A45B0', // active/pressed — deeper indigo
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#2563EB', // fresh cobalt blue — secondary actions / links
    light: '#DCE7FE',
    dark: '#1D4FD7',
    contrastText: '#FFFFFF', // 4.9:1, AA
  },
  // White passes AA on all three light mains (success 4.54:1, warning 5.02:1,
  // error 4.83:1), so they carry white text and keep their conventional hue.
  success: { main: '#15803D', light: '#DCF7E3', dark: '#0F5C2E', contrastText: '#FFFFFF' },
  warning: { main: '#B45309', light: '#FDEBD3', dark: '#92400E', contrastText: '#FFFFFF' },
  error: { main: '#DC2626', light: '#FEE2E2', dark: '#B91C1C', contrastText: '#FFFFFF' },
  info: { main: '#2563EB', light: '#DCE7FE', dark: '#1D4FD7', contrastText: '#FFFFFF' }, // blue family, white on blue (4.9:1, AA)
  // Custom token groups (module-augmented in types.d.ts)
  accent: {
    highlight: '#4F5BD5', // the indigo — sparing highlight/CTA accent only
    highlightStrong: '#3A45B0', // AA-safe deeper indigo for accent text on light
  },
  cat: {
    // Bright accent hues — identical in both modes. Used only for the
    // Saved-Workflows category badges; left vivid so categories stay distinct.
    marketResearch: '#00C2FF', // electric cyan
    clauseLogic: '#9B5CFF', // violet
    sowVerification: '#FF6A3D', // coral
    wageDet: '#FFB800', // amber
    pnm: '#FF4D8D', // magenta
    fopr: '#3D5AFE', // indigo
    services: '#00D98B', // emerald
    gpc: '#14B8A6', // teal
  },
  diff: {
    added: '#2E7D32', // green
    removed: '#D32F2F', // red
    selected: '#2196F3', // blue
  },
  marketResearch: {
    // Red is the explicit cross-party edit-awareness cue. Every marker also
    // carries a non-color accessible label, so it is never color-only state.
    editAwareness: '#C62828',
  },
  surfaceMuted: '#F0F2F7', // soft cool muted gray (table headers, row hover base)
};

export const darkPalette: PaletteOptions = {
  mode: 'dark',
  background: {
    default: '#0B0D12', // cool near-black canvas (faint blue cast)
    paper: '#15171E', // cool charcoal card
  },
  divider: '#262A35', // cool charcoal 1px border
  text: {
    primary: '#F4F5F8', // cool near-white
    secondary: '#A6ACBE', // cool light slate
    disabled: '#6A7080',
  },
  primary: {
    // Lifted bright periwinkle indigo. As OUTLINED text/border it reads strongly
    // on the near-black canvas (~6.4:1). As a contained fill, white fails AA on
    // this bright tone (~3:1), so contrastText is near-black (#0A0F2C, ~6.2:1) —
    // matching the palette's bright-fill/dark-text convention.
    main: '#818CF8',
    light: '#9AA4FF', // hover
    dark: '#6670E0', // active/pressed
    contrastText: '#0A0F2C', // near-black on bright indigo (~6.2:1, AA)
  },
  secondary: {
    main: '#60A5FA', // lifted bright blue for dark bg
    light: '#2A3A66',
    dark: '#3B82F6',
    contrastText: '#07112E', // dark on bright blue, AA
  },
  success: { main: '#2BE38A', light: '#103024', dark: '#16A34A', contrastText: '#03190C' },
  warning: { main: '#FFC633', light: '#3A2E12', dark: '#D69800', contrastText: '#1A1300' },
  // red-500; white fails as a fill (3.76:1) so near-black contrastText carries it.
  error: { main: '#EF4444', light: '#3A1F1F', dark: '#B91C1C', contrastText: '#2A0A10' },
  info: { main: '#60A5FA', light: '#11233E', dark: '#3B82F6', contrastText: '#07112E' }, // blue family
  accent: {
    highlight: '#818CF8', // bright indigo — sparing highlight/CTA accent only
    highlightStrong: '#818CF8', // already bright — full strength on near-black
  },
  cat: {
    // Bright accent hues — identical to light mode.
    marketResearch: '#00C2FF', // electric cyan
    clauseLogic: '#9B5CFF', // violet
    sowVerification: '#FF6A3D', // coral
    wageDet: '#FFB800', // amber
    pnm: '#FF4D8D', // magenta
    fopr: '#3D5AFE', // indigo
    services: '#00D98B', // emerald
    gpc: '#14B8A6', // teal
  },
  diff: {
    added: '#66BB6A',
    removed: '#EF5350',
    selected: '#64B5F6',
  },
  marketResearch: {
    // Bright red retains contrast against the charcoal stepper surface.
    editAwareness: '#FF6B6B',
  },
  surfaceMuted: '#1A1D26', // cool muted dark
};

export function selectPalette(mode: ColorMode): PaletteOptions {
  return mode === 'dark' ? darkPalette : lightPalette;
}
